import { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { Triangle } from 'lucide-react-native';
import { Post, normalizePostCategory } from '../../types';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, ELEVATION, ON_COLOR } from '../../theme';
import { formatRelativeTime } from '../../utils/time';
import { CategoryPinGlyph } from './CategoryPinGlyph';
import { useTabBarClearance } from '../../hooks/useTabBarClearance';

// Web-only preview shown when the mouse hovers an observation dot on the map.
// Presentational only: every field comes from the Post already in memory, so
// there is no fetch. Reuses the in-memory `thumbnailUrl || imageUrl` (same
// fallback PostCard uses) to keep the hover instant. The single hero image
// upscales ~1.25x from the 400x400 thumbnail at this card width — an
// intentional trade to avoid a fetch on hover; before/after halves stay 1:1
// crisp since each renders at half-width.

const CARD_WIDTH = 500;
const IMAGE_HEIGHT = 340;
// Rough content height below the image (category row + up-to-3-line caption +
// meta + padding). Only the first-frame fallback for placement math — the
// wrapper's onLayout replaces it with the real height, since positioning off
// an over-estimate leaves the card (and its caret) floating short of the pin.
const BODY_HEIGHT = 120;
const CARD_HEIGHT = IMAGE_HEIGHT + BODY_HEIGHT;
const DOT_RADIUS = 14; // matches the 28px marker in useMarkerLayer.web
const GAP = 12; // breathing room between dot and card
const EDGE = 8; // keep the card off the very edge of the map
// Callout caret: a rotated square half-tucked behind the card, protruding
// CARET/2 into the gap toward the dot so the card visibly points at the pin
// it describes.
const CARET = 14;

interface MapPostHoverCardProps {
  post: Post;
  point: { x: number; y: number };
  containerSize: { width: number; height: number };
  /** Extra left inset to clear a left-docked panel (the defense plan
   *  editor/viewer). Unlike containerSize's rightDockInset shave, the
   *  container's own left edge doesn't move, so this is a floor on the
   *  left clamp bound rather than a change to the width passed in. 0 off
   *  the desktop shell, where nothing docks. */
  leftInset?: number;
}

export function MapPostHoverCard({
  post,
  point,
  containerSize,
  leftInset = 0,
}: MapPostHoverCardProps) {
  const { colors } = useTheme();

  // Clearance the floating tab bar reserves at the bottom of the map
  // container — 0 when it isn't actually on screen. Folded into the bottom
  // clamp bound so a below-the-pin placement can never land under the nav.
  const tabBarClearance = useTabBarClearance();

  // Real rendered height (content-sized: caption is 0-3 lines). Estimate on
  // the first frame, corrected by onLayout below.
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const cardHeight = measuredHeight ?? CARD_HEIGHT;

  const category = normalizePostCategory(post.category);
  const accent =
    category === 'progress'
      ? colors.mapMarkerProgress
      : category === 'news'
        ? colors.mapMarkerNews
        : colors.mapMarkerOpportunity;
  const displayName = post.isAnonymous ? 'Fire Scout' : post.authorName;
  // News posts are text-first and may carry no photo at all — the hover
  // card just skips the image block for those (see hasImage below).
  const displayImage = post.thumbnailUrl || post.imageUrl;
  const beforeImage =
    post.beforeThumbnailUrl ||
    post.beforeImageUrl ||
    post.beforePostThumbnailUrl ||
    post.beforePostImageUrl;
  const hasBeforeAfter = Boolean(beforeImage);
  const hasImage = hasBeforeAfter || Boolean(displayImage);
  const timestamp = formatRelativeTime(post.createdAt, { suffix: 'ago' });

  // Card can't exceed the viewport — clamp width before using it for the
  // horizontal-center and left-clamp math below, so narrow windows shrink
  // the card instead of overflowing it. Also shave off the left-docked
  // panel's inset, the same way the width Map.web.tsx passes in already
  // shaves off the right-docked one.
  const cardWidth = Math.min(CARD_WIDTH, containerSize.width - leftInset - 2 * EDGE);

  // Center horizontally on the dot, then clamp so edge-of-map posts don't
  // clip — and so the card never lands under a left-docked panel.
  const left = clamp(
    point.x - cardWidth / 2,
    EDGE + leftInset,
    Math.max(EDGE + leftInset, containerSize.width - cardWidth - EDGE)
  );

  // Prefer sitting above the dot; flip below when there isn't room up top —
  // but not into the floating tab bar's reserved band: if a below placement
  // would land under it, prefer above (and let the top clamp keep it
  // on-screen) rather than clamping a "below" card up into the pin.
  const above = point.y - DOT_RADIUS - GAP - cardHeight;
  const below = point.y + DOT_RADIUS + GAP;
  const fitsAbove = above >= EDGE;
  const fitsBelowWithClearance =
    below + cardHeight <= containerSize.height - tabBarClearance - EDGE;
  const placedAbove = fitsAbove || !fitsBelowWithClearance;
  const top = clamp(
    placedAbove ? above : below,
    EDGE,
    Math.max(EDGE, containerSize.height - tabBarClearance - cardHeight - EDGE)
  );

  // Keep the caret on the dot's x even when the card slid to a clamped
  // left, staying inside the straight run of the edge (off the rounded
  // corners).
  const caretLeft = clamp(point.x - left - CARET / 2, SPACING.md, cardWidth - CARET - SPACING.md);

  return (
    <View
      testID="map-post-hover-card"
      pointerEvents="none"
      onLayout={(e) => setMeasuredHeight(e.nativeEvent.layout.height)}
      style={[styles.wrapper, { left, top, width: cardWidth }]}>
      {/* Rendered before the card surface so only the half protruding past
          the edge is visible — surface + border match the card so the caret
          reads as the card's own tail pointing at the dot. */}
      <View
        testID="map-post-hover-caret"
        style={[
          styles.caret,
          placedAbove ? styles.caretBottom : styles.caretTop,
          { backgroundColor: colors.surfaceElevated, borderColor: colors.border, left: caretLeft },
        ]}
      />
      <View
        style={[
          styles.card,
          ELEVATION.lg,
          {
            backgroundColor: colors.surfaceElevated,
            borderColor: colors.border,
          },
        ]}>
        {hasBeforeAfter ? (
          <View style={styles.pairRow}>
            <View style={styles.pairHalf}>
              <Image
                source={{ uri: beforeImage }}
                style={styles.pairImage}
                contentFit="cover"
                transition={120}
              />
              <View style={[styles.cornerLabel, { backgroundColor: colors.overlay }]}>
                <Text style={styles.cornerLabelText}>Before</Text>
              </View>
            </View>
            <View style={styles.pairHalf}>
              <Image
                source={{ uri: displayImage }}
                style={styles.pairImage}
                contentFit="cover"
                transition={120}
              />
              <View style={[styles.cornerLabel, { backgroundColor: colors.overlay }]}>
                <Text style={styles.cornerLabelText}>After</Text>
              </View>
            </View>
          </View>
        ) : hasImage ? (
          <Image
            source={{ uri: displayImage }}
            style={styles.image}
            contentFit="cover"
            transition={120}
          />
        ) : null}
        <View style={styles.body}>
          <View style={styles.categoryRow}>
            {/* Mini pin matches the marker this card annotates, rather than a
              plain color swatch: a dot + white category glyph normally, or a
              solid delta triangle for before/after posts — the marker swaps
              its whole shape for those, not just a corner badge. */}
            {hasBeforeAfter ? (
              <Triangle
                size={16}
                color={ON_COLOR.fill}
                fill={colors.mapMarkerDelta}
                strokeWidth={2}
              />
            ) : (
              <View style={[styles.categoryPin, { backgroundColor: accent }]}>
                <CategoryPinGlyph
                  category={category}
                  size={9}
                  strokeWidth={3}
                  color={ON_COLOR.fill}
                />
              </View>
            )}
            <Text style={[styles.categoryLabel, { color: accent }]}>
              {category === 'progress' ? 'Progress' : category === 'news' ? 'News' : 'Opportunity'}
            </Text>
            {hasBeforeAfter ? (
              <View style={styles.deltaIndicator}>
                <Triangle
                  size={10}
                  color={ON_COLOR.fill}
                  fill={colors.mapMarkerDelta}
                  strokeWidth={1.5}
                />
                <Text style={[styles.categoryLabel, { color: colors.mapMarkerDelta }]}>
                  Before / after
                </Text>
              </View>
            ) : null}
          </View>
          {post.caption ? (
            <Text numberOfLines={3} style={[styles.caption, { color: colors.text }]}>
              {post.caption}
            </Text>
          ) : null}
          <Text numberOfLines={1} style={[styles.meta, { color: colors.textTertiary }]}>
            {displayName}
            {timestamp ? ` · ${timestamp}` : ''}
          </Text>
        </View>
      </View>
    </View>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    // width is set inline per-render (clamped to the container) — see
    // `cardWidth` above.
    // Stacking tiers (same context, so numeric order is all that matters):
    // Leaflet panes (~600) < GlobalFloatingTabBar (1000) < this card / map
    // controls (1100, matching MapControls.tsx). The tab bar mounts after
    // the map in app/_layout.tsx, so at a tied z-index it paints on top —
    // this has to clear 1000, not just match it. The card is
    // pointerEvents:none regardless, so it never blocks the controls.
    zIndex: 1100,
  },
  card: {
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    // Clips the images to the rounded corners — which is exactly why the
    // caret lives on the wrapper, outside this clip.
    overflow: 'hidden',
  },
  caret: {
    position: 'absolute',
    width: CARET,
    height: CARET,
    borderRadius: 3,
    borderWidth: 1,
    transform: [{ rotate: '45deg' }],
  },
  caretTop: {
    top: -CARET / 2,
  },
  caretBottom: {
    bottom: -CARET / 2,
  },
  image: {
    width: '100%',
    height: IMAGE_HEIGHT,
  },
  pairRow: {
    flexDirection: 'row',
  },
  pairHalf: {
    flex: 1,
  },
  pairImage: {
    width: '100%',
    height: IMAGE_HEIGHT,
  },
  cornerLabel: {
    position: 'absolute',
    top: SPACING.xs,
    left: SPACING.xs,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.xs,
    paddingVertical: 2,
  },
  cornerLabelText: {
    ...TYPOGRAPHY.labelSmall,
    color: ON_COLOR.text,
  },
  body: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    gap: SPACING.xxs,
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  categoryPin: {
    width: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryLabel: {
    ...TYPOGRAPHY.labelSmall,
  },
  deltaIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xxs,
    marginLeft: SPACING.xs,
  },
  caption: {
    ...TYPOGRAPHY.body,
  },
  meta: {
    ...TYPOGRAPHY.bodySmall,
  },
});
